import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  FormControl,
  MenuItem,
  Select,
  Divider,
  TextField,
  Autocomplete,
  Chip,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../components/loading/loading";
import { getCreateIssueSearchOrderRequest } from "../../../../redux/orders/slice";
import { addOrderIssueRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import eventBus from "../../../../utils/EventBus";
import { userType } from "../../../../utils/Constant";

export default function AddInspectorAndClientIssueDialog({
  orders,
  showDialog,
  closeDialog,
}: any) {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [orderOptions, setOrderOptions] = useState<string[]>([]);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { createIssueSearchResults } = useSelector(
    (state: any) => state.orders
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  const validationSchema = Yup.object({
    subType: Yup.string().required("This field can't be empty"),
    orderIds: Yup.array().min(1, "This field can't be empty"),
    orderType: Yup.string(),
    description: Yup.string().required("This field can't be empty"),
    onHoldUntilResolved: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      subType: "",
      orderIds: [] as string[],
      orderType: "individual",
      description: "",
      onHoldUntilResolved: true,
      autoCloseIssue: false,
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      const payload: any = {
        subType: values.subType,
        orderIds: values.orderIds,
        createIndividualIssues:
          values.orderType === "individual" ? true : false,
        description: values.description,
        onHoldUntilResolved: values.onHoldUntilResolved,
        isResolved: values.autoCloseIssue,
      };
      addIssue(payload);
    },
  });

  useEffect(() => {
    if (orders) {
      const reducedOrders = orders.map((order: any) =>
        order?.orderNumber.toString()
      );
      setSelectedOrderNumber(reducedOrders);
      setSelectedOrders(orders);
    }
  }, [orders]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      if (searchText) {
        dispatch(
          getCreateIssueSearchOrderRequest({
            searchTerm: searchText.trim(),
          })
        );
      } else {
        setOrderOptions([]);
      }
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]); // eslint-disable-line

  useEffect(() => {
    if (createIssueSearchResults) {
      const reducedOrders = createIssueSearchResults.map((order: any) =>
        order?.orderNumber.toString()
      );
      setOrderOptions(reducedOrders);
    }
  }, [createIssueSearchResults]);

  useEffect(() => {
    const orderIds = selectedOrders?.map((order) => order?.id);
    formik.setFieldValue("orderIds", orderIds);
  }, [selectedOrders]); // eslint-disable-line

  const addIssue = async (payload: any) => {
    setShowLoading(true);
    const response = await addOrderIssueRequest(payload);
    setShowLoading(false);
    if (response?.status === 201) {
      eventBus.emit("createIssuesSuccess");
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Dialog
          open={showDialog}
          onClose={closeDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth={true}
          maxWidth={"lg"}
        >
          <DialogTitle id="responsive-dialog-title">
            <Typography variant="h5" color="black">
              Add Issue
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: "black",
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers>
            <DialogContentText id="alert-dialog-description">
              <Box>
                <Box className="display-name-layout">
                  <Typography className="label-txt">Issue Type</Typography>
                  <FormControl fullWidth>
                    {currentUser?.userType === userType.inspector ? (
                      <Select
                        labelId="issue-type-label"
                        id="issue-type"
                        name="subType"
                        value={formik.values.subType}
                        onChange={formik.handleChange}
                      >
                        <MenuItem value={"Acceptance"}>
                          Acceptance Issue
                        </MenuItem>
                        <MenuItem value={"ContractorIssue"}>
                          Contractor Issue
                        </MenuItem>
                      </Select>
                    ) : (
                      <Select
                        labelId="issue-type-label"
                        id="issue-type"
                        name="subType"
                        value={formik.values.subType}
                        onChange={formik.handleChange}
                      >
                        <MenuItem value={"Update"}>Update Issue</MenuItem>
                      </Select>
                    )}
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Selected Orders</Typography>
                  <FormControl
                    fullWidth
                    error={
                      (formik.touched.orderIds || formik.submitCount > 0) &&
                      Boolean(formik.errors.orderIds)
                    }
                  >
                    <Autocomplete
                      id="order-numbers"
                      multiple
                      options={orderOptions}
                      value={selectedOrderNumber}
                      onChange={(event, newValue) => {
                        setSelectedOrderNumber(newValue);
                        const allOrders = [
                          ...selectedOrders,
                          ...createIssueSearchResults,
                        ];
                        const filterOrderNumbers = newValue.map(Number);
                        const filtered = allOrders.filter((order) =>
                          filterOrderNumbers.includes(order.orderNumber)
                        );
                        setSelectedOrders(filtered);
                        setOrderOptions([]);
                      }}
                      freeSolo
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => (
                          <Chip
                            sx={{ height: 28, borderWidth: 1.5 }}
                            variant="outlined"
                            label={option}
                            color="primary"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      )}
                    />
                    <FormHelperText>
                      {(formik.touched.orderIds || formik.submitCount > 0) &&
                        formik.errors.orderIds}
                    </FormHelperText>

                    {selectedOrderNumber.length > 1 && (
                      <RadioGroup
                        sx={{ mt: 1, color: "rgba(0, 0, 0, 0.87)" }}
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="orderType"
                        value={formik.values.orderType}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="individual"
                          control={<Radio />}
                          label="Create individual issues for each selected order"
                        />
                        <FormControlLabel
                          value="group"
                          control={<Radio />}
                          label="Attach all selected orders (with the same Inspector or client) to a single issue "
                        />
                      </RadioGroup>
                    )}
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Issue</Typography>
                  <TextField
                    name="description"
                    fullWidth
                    id="outlined-basic"
                    label="Describe the Issue"
                    variant="outlined"
                    multiline
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    rows={2}
                    error={
                      (formik.touched.description || formik.submitCount > 0) &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      (formik.touched.description || formik.submitCount > 0) &&
                      formik.errors.description
                    }
                  />
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">
                    Auto-Close Issue?
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        name="autoCloseIssue"
                        checked={formik.values.autoCloseIssue}
                        onChange={formik.handleChange}
                      />
                    }
                    label={formik.values.autoCloseIssue ? "Yes" : "No"}
                  />
                </Box>
                <Divider />
                <Typography sx={{ fontStyle: "italic", mt: 1 }}>
                  This job will be temporarily held and will not be assigned to
                  any other inspector until the issue is reviewed by Spectrum.
                </Typography>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeDialog} type="reset">
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              disabled={!formik.isValid}
            >
              Create Issue
            </Button>
          </DialogActions>
        </Dialog>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoading && <Loading />}
    </>
  );
}
